import Dialog from './Dialog';
import { IconDoc } from './dialogIcons';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useOrg } from './OrgContext';
import { useToast } from './ToastContext';

/*
  The two things a site has to say to somebody before they get on with their
  day: what changed, and what they are agreeing to.

  **They are no longer the same kind of thing, and that is the owner's own
  decision of 5 September 2026.** In his words: *"I think it should only appear
  the first login after a system wide update i done on the home screen and then
  the user can clikc a i acknoledge button and it goes away."*

    **The terms BLOCK.** They are the one thing in OnHand you genuinely cannot
    walk away from, because somebody working without having agreed to them is
    the whole problem terms exist to solve. They have no close mark, and a cross
    that does nothing is worse than no cross at all.

    **The release note does not block any more.** It is a thing you are TOLD,
    not a thing you agree to, so it became a notice at the top of Home with one
    button that clears it. Until 10 September 2026 it was a box over a dimmed
    page that had to be closed before anybody could do anything.

  **One call still answers both**, which is why this file holds them together
  even though they now appear in different places. The answer is almost always
  "nothing", and that should cost one trip to the server rather than two.

  So this is a provider: it does the asking, it draws the terms itself because
  they cover everything, and it hands the release note to Home, which is where
  the owner asked for it.
*/

const Ctx = createContext(null);
const NOTHING = { note: null, busy: false, dismiss: () => {} };

export function useReleaseNote() {
  return useContext(Ctx) || NOTHING;
}

export function AnnouncementsProvider({ children }) {
  const { user, signOut } = useAuth();
  const { current } = useOrg();
  const toast = useToast();
  const [note, setNote]   = useState(null);
  const [terms, setTerms] = useState(null);
  const [busy, setBusy]   = useState(false);

  const orgId = current?.id || null;

  const load = useCallback(async () => {
    if (!user || !orgId) { setNote(null); setTerms(null); return; }
    const { data, error } = await supabase.rpc('pending_announcements');
    // A site that has never published anything is the ordinary case, and a
    // failed ask is not worth interrupting anybody over - it is a notice, not
    // the work. Stay quiet and try again next sign-in.
    if (error) return;
    setTerms(data?.terms || null);
    setNote(data?.release_note || null);
  }, [user, orgId]);

  useEffect(() => { load(); }, [load]);

  async function acceptTerms() {
    if (!terms) return;
    setBusy(true);
    const { error } = await supabase.rpc('accept_terms', { terms_id: terms.id });
    setBusy(false);
    /*
      **It stays up when the write failed**, which is the point. Clearing the
      screen on a refused write would let somebody work without having agreed,
      and the record would say they never agreed, which is the worst of both.
    */
    if (error) {
      toast.error('That could not be recorded, so the terms are still waiting. Try again.');
      return;
    }
    setTerms(null);
  }

  /*
    **Reading a note back is proved, not assumed.** This project's own lesson,
    written after marking a notification read changed no rows, raised nothing,
    and answered "fine" while the screen ticked it off: a save is proved by the
    absence of an error only when the write is one the rules allow. This one
    raises when it is refused, so the error is real, and the note stays on Home
    when it comes back rather than vanishing until the next sign-in brings it
    back and makes OnHand look broken.
  */
  const dismiss = useCallback(async () => {
    if (!note) return;
    setBusy(true);
    const { error } = await supabase.rpc('mark_release_note_seen', { note_id: note.id });
    setBusy(false);
    if (error) {
      toast.error('That could not be recorded, so this note will be here again next time you sign in.');
      return;
    }
    setNote(null);
  }, [note, toast]);

  return (
    <Ctx.Provider value={{ note, busy, dismiss }}>
      {children}
      {terms && (
        <Dialog
          kind="info" icon={<IconDoc />} size="list"
          title="Terms and Conditions"
          record={'Version ' + terms.version}
          footer={
            <>
              {/* **The one way out, for somebody who will not accept**
                  (the audit after the rebrand, issue 29, 18 September 2026).
                  The terms still block, which is the owner's decision; they
                  had no exit at all, so the only way to leave was to close the
                  browser, and the account stayed signed in behind them. */}
              <button className="btn btn-quiet" disabled={busy} onClick={() => signOut()}>
                Sign out
              </button>
              <button className="btn btn-primary" disabled={busy} onClick={acceptTerms}>
                {busy ? 'Saving...' : 'I accept'}
              </button>
            </>
          }
        >
          {/* Group H's line, above the terms themselves. It says why they are in
              the way, which is the one question somebody has when a screen they
              did not ask for is between them and their work. */}
          <div className="an-lead">
            These have changed since you last signed in. Reading them is the last
            thing between you and your work.
          </div>
          <div className="an-scroll">{terms.body}</div>
        </Dialog>
      )}
    </Ctx.Provider>
  );
}
